/**
 * 太阳码 scene 参数解析拦截器
 *
 * 功能：
 * 1. 在页面加载时检查 scene 参数是否为指纹码
 * 2. 如果是指纹码，调用接口获取指纹详情，从 extraJson 中还原业务参数
 * 3. 将业务参数合并到页面的 options 中，对业务页面透明
 *
 * 使用场景：
 * - 扫描太阳码进入小程序时，scene 参数为指纹码
 * - 页面可以直接从 options 中获取业务参数（如 societyId、inviteCode 等）
 */

import { getFingerprintDetailApi } from '@/components/SharePopupV2/api/index'

// 需要解析 scene 的页面路径列表（太阳码落地页）
const SCENE_PARSE_PAGES = [
  'pages/index/index', // 领地分享
  'subPackages/hk-customer-user/pages/redpacket/show', // 红包分享
  'subPackages/hk-customer-ec/pages/storeHome/index', // 店铺分享
  'subPackages/hk-customer-ec/pages/shopwindowState/index', // 橱窗分享
  'subPackages/hk-customer-user/pages/userState/index', // 用户主页分享
  'subPackages/hk-customer-ec/pages/productDetails/index', // 商品分享
  'subPackages/hk-customer-content/pages/contentDetail/indexV2', // 内容分享
  'subPackages/hk-customer-content/pages/landDetail/indexWx' //领地落地页
]

// 缓存已解析的 scene，避免重复请求
const parsedSceneCache = new Map<string, Record<string, string>>()

/**
 * 判断 scene 是否为指纹码格式
 * 指纹码通常是一个较短的字符串，不包含 = 和 &
 *
 * @param scene scene 参数
 * @returns 是否为指纹码
 */
export function isFingerprintCode(scene: string): boolean {
  if (!scene) return false
  
  // 去除首尾空白字符
  const trimmedScene = scene.trim()
  
  console.log('[isFingerprintCode] 检查 scene:', {
    original: scene,
    trimmed: trimmedScene,
    length: trimmedScene.length,
    hasEqual: trimmedScene.includes('='),
    hasAmpersand: trimmedScene.includes('&'),
  })
  
  // 指纹码特征：
  // 1. 不包含 = 和 &（普通参数格式为 key=value&key2=value2）
  // 2. 长度在合理范围内（通常 10-100 字符，放宽限制）
  // 3. 或者以 PF 开头（推广指纹码的特征）
  const isFingerprint = 
    (!trimmedScene.includes('=') && !trimmedScene.includes('&') && trimmedScene.length >= 10 && trimmedScene.length <= 100) ||
    trimmedScene.startsWith('PF')
  
  console.log('[isFingerprintCode] 判断结果:', isFingerprint)
  
  return isFingerprint
}

/**
 * 解析指纹码获取业务参数
 *
 * @param fingerprintCode 指纹码
 * @returns 业务参数对象
 */
export async function parseFingerprintCode(
  fingerprintCode: string
): Promise<Record<string, string> | null> {
  // 检查缓存
  if (parsedSceneCache.has(fingerprintCode)) {
    return parsedSceneCache.get(fingerprintCode) || null
  }

  try {
    const detail = await getFingerprintDetailApi(fingerprintCode)

    if (!detail || !detail.promotionParamJson) {
      console.warn('[SceneParser] 指纹详情为空')
      return null
    }

    // 解析 promotionParamJson
    const parsedJson = JSON.parse(detail.promotionParamJson)
    const extraJson = parsedJson.extraJson || {}

    // 将所有值转换为字符串（页面 options 中的值都是字符串）
    const businessParams: Record<string, string> = {}
    for (const [key, value] of Object.entries(extraJson)) {
      businessParams[key] = String(value)
    }

    // 添加指纹码到参数中，供推广追踪使用
    businessParams.promotionfingerprintId = fingerprintCode

    // 缓存结果
    parsedSceneCache.set(fingerprintCode, businessParams)

    console.log('[SceneParser] 解析指纹码成功:', businessParams)
    return businessParams
  } catch (error) {
    console.error('[SceneParser] 解析指纹码失败:', error)
    return null
  }
}

/**
 * 处理页面 onLoad 时的 scene 参数解析
 * 在页面 onLoad 之前调用，将业务参数合并到 options 中
 *
 * @param options 页面 options
 * @param pagePath 页面路径
 * @returns 处理后的 options
 */
export async function parseSceneOptions(
  options: Record<string, string | undefined>,
  pagePath: string
): Promise<Record<string, string | undefined>> {
  // #ifndef MP-WEIXIN
  return options
  // #endif

  // #ifdef MP-WEIXIN
  // 检查是否需要解析 scene
  const normalizedPath = pagePath.startsWith('/') ? pagePath.slice(1) : pagePath
  if (!SCENE_PARSE_PAGES.includes(normalizedPath)) {
    return options
  }

  // 获取 scene 参数
  let scene = options.scene || ''
  if (!scene) {
    return options
  }

  // 解码 scene
  try {
    scene = decodeURIComponent(scene)
  } catch {
    // 解码失败，使用原值
  }

  // 判断是否为指纹码
  if (!isFingerprintCode(scene)) {
    // 不是指纹码，可能是普通的 key=value 格式，按原逻辑处理
    return options
  }

  // 解析指纹码获取业务参数
  const businessParams = await parseFingerprintCode(scene)
  if (!businessParams) {
    return options
  }

  // 合并业务参数到 options（业务参数优先级低于原有参数）
  const mergedOptions = { ...businessParams, ...options }

  console.log('[SceneParser] 合并后的 options:', mergedOptions)
  return mergedOptions
  // #endif
}

/**
 * 同步版本的 scene 解析（用于拦截器）
 * 由于拦截器不支持异步，这里只做标记，实际解析在页面 onLoad 时进行
 *
 * @param url 页面 URL
 * @returns 是否需要解析 scene
 */
export function needParseScene(url: string): boolean {
  // #ifndef MP-WEIXIN
  return false
  // #endif

  // #ifdef MP-WEIXIN
  if (!url) return false

  const [path, queryStr] = url.split('?')
  const normalizedPath = path.startsWith('/') ? path.slice(1) : path

  // 检查是否在需要解析的页面列表中
  if (!SCENE_PARSE_PAGES.includes(normalizedPath)) {
    return false
  }

  // 检查是否有 scene 参数
  if (!queryStr) return false

  const params: Record<string, string> = {}
  queryStr.split('&').forEach((item) => {
    const [k, v = ''] = item.split('=')
    if (k) params[k] = v
  })

  const scene = params.scene
  if (!scene) return false

  // 解码并判断是否为指纹码
  let decodedScene = scene
  try {
    decodedScene = decodeURIComponent(scene)
  } catch {
    // 解码失败，使用原值
  }

  return isFingerprintCode(decodedScene)
  // #endif
}

/**
 * 清除解析缓存
 */
export function clearParseCache(): void {
  parsedSceneCache.clear()
}

/**
 * 获取已解析的业务参数（从缓存中）
 *
 * @param fingerprintCode 指纹码
 * @returns 业务参数对象
 */
export function getCachedBusinessParams(
  fingerprintCode: string
): Record<string, string> | null {
  return parsedSceneCache.get(fingerprintCode) || null
}

export default {
  parseSceneOptions,
  needParseScene,
  clearParseCache,
  getCachedBusinessParams,
  isFingerprintCode,
  parseFingerprintCode,
}
